/**
 * Lenient RPE chart decoder and the fragment decoder of the Raw panel (W1-07a formats-json-rpe; contract
 * formats/rpe/json.ts `DecodeRpeFn`, `DecodeRpeFragmentFn`).
 *
 * `decodeRpe` accepts what Phira accepts and more, recording every deviation (rpe-format.md F§0-F§13):
 *  - bytes: strict UTF-8 first; on failure a lenient decode plus 'invalidUtf8' (Phira would detect the binary PBC
 *    format and fail). A BOM and leading whitespace are accepted and reported ('bom', 'leadingWhitespace': Phira
 *    cannot load a chart with a BOM, and misdetects leading whitespace without `format: rpe`); a text without the
 *    substring "META" is reported ('noMetaSubstring': Phira would not detect it as RPE);
 *  - structure and types through the schema-driven readers (read.ts): float in an integer field (`-0` included,
 *    which serde_json reads as a float), missing required field, wrong type, negative unsigned, c = 0, attachUI
 *    that is not one of the seven names, control points without their value key, with non-numeric keys or with
 *    `x` / `easing` twice, unpaired surrogate escapes (replaced by U+FFFD), explicit `null` kept as null;
 *  - META.RPEVersion as Phira computes it (F§2.2: a JSON integer through as_i64, a float -> 160, a string through
 *    i32 parsing, anything else -> 160) plus the exact source lexeme of a float or string version, so that
 *    `170.0` (or `-0`) can be written back verbatim and stays effective 160 in Phira.
 * It throws only when the text is not JSON (`RpeJsonSyntaxError`, with position and a translatable reason).
 *
 * `decodeRpeFragment` reads one event list, a list of notes, one line's own fields or one control curve with the
 * same readers and normalisation rules. A fragment that normalisation would change is reported, not silently
 * repaired: `ir` is null then, and `decodeRpeFragmentDetailed` additionally returns the report and the repaired
 * IR (`normalized`) so the panel can offer "Apply the corrected version". The report follows the whole-chart
 * rules: decoder diagnostics become entries ('changed' / 'normalized', as in normalizeRpe) next to what list,
 * note, line and control normalisation changed, so a value the decoder replaced or rounded never reaches `ir`.
 * `normalized` always encodes (encodeRpeFragment), and `value` is always plain JSON (numbers beyond the f64 range
 * hold the largest finite double of their sign, unpaired surrogates U+FFFD, as in data a whole chart keeps).
 */

import type { ImportReportEntry } from '../../core/ir/types';
import { Beats } from '../../core/time/beat';
import { createTempoMap } from '../../core/time/tempo';
import type { Triple } from '../../core/time/types';
import type { Json, MessageRef } from '../../core/util/types';
import type { RawNumberLexeme } from '../../core/model/types';
import {
  hasLoneSurrogate,
  isSerdeIntegerLexeme,
  jsonSyntaxErrorRef,
  parseJsonLossless,
  stringTokenAt,
} from '../json/lexer';
import type { JsonNode, JsonSyntaxError } from '../json/types';
import type {
  DecodeDiagnostic,
  DecodeRpeFn,
  DecodeRpeFragmentFn,
  RpeFragmentIr,
  RpeFragmentKind,
  RpeNote,
} from './json';
import {
  controlsToIr,
  defaultTextureKind,
  fatherNeverValid,
  lineFieldsToIr,
  listTime,
  normalizeNotes,
  reportDiagnostics,
} from './normalize';
import type { NormEnv, RpeSpeedMode } from './normalize';
import { normalizeEventList } from './normEvents';
import type { ListMode, ListValue, ValueKind } from './normEvents';
import { NormReport } from './normReport';
import {
  ObjView,
  passthroughJson,
  Reader,
  readChart,
  readControls,
  readEventList,
  readLineFields,
  readNote,
} from './read';
import { CONTROL_CURVES, eventValueKind } from './schema';
import type { ControlValueKey, EventListKey } from './schema';

/** Thrown by the decoders when the text is not JSON. */
export class RpeJsonSyntaxError extends Error {
  readonly error: JsonSyntaxError;
  /** Translatable reason with line and column (namespace 'json'). */
  readonly reason: MessageRef;
  constructor(error: JsonSyntaxError) {
    super(`Not valid JSON at line ${String(error.line)}, column ${String(error.col)}: ${error.message}`);
    this.name = 'RpeJsonSyntaxError';
    this.error = error;
    this.reason = jsonSyntaxErrorRef(error);
  }
}

/**
 * Translatable description of a decode diagnostic (namespace 'rpe', keys `rpe.diag.*`). Params: `path` (the JSON
 * path, "(root)" for the document), `detail` (raw source lexeme or value, '' when there is none), `expected` and
 * `found` (language-neutral kind codes from RpeDiagnostic, '' when unknown; the messages translate them with
 * ICU `select`).
 */
export function diagnosticRef(d: DecodeDiagnostic): MessageRef {
  const x = d;
  const detail = d.detail ?? '';
  const params = {
    path: d.path === '' ? '(root)' : d.path,
    detail,
    expected: x.expected ?? '',
    found: x.found ?? '',
  };
  switch (d.code) {
    case 'wrongType':
      if (x.reason === 'duplicateKey') return { key: 'rpe.diag.duplicateKey', params };
      if (x.reason === 'outOfRange') return { key: 'rpe.diag.outOfRange', params };
      if (x.reason === 'numberOverflow') return { key: 'rpe.diag.numberOverflow', params };
      if (x.reason === 'otherFragment') return { key: 'rpe.diag.otherFragment', params };
      if (x.reason === 'unpairedSurrogate') return { key: 'rpe.diag.unpairedSurrogate', params };
      if (x.reason === 'notKept') return { key: 'rpe.diag.notKept', params };
      return { key: detail === '' ? 'rpe.diag.wrongType' : 'rpe.diag.wrongTypeValue', params };
    case 'unknownAttachUI':
      return { key: detail === '' ? 'rpe.diag.unknownAttachUIKind' : 'rpe.diag.unknownAttachUI', params };
    case 'controlNonNumericExtra':
      return {
        key: detail === '' ? 'rpe.diag.controlNonNumericExtra' : 'rpe.diag.controlNonNumericExtraValue',
        params,
      };
    case 'floatInIntField':
      return { key: 'rpe.diag.floatInIntField', params };
    case 'missingRequired':
      return { key: 'rpe.diag.missingRequired', params };
    case 'negativeUnsigned':
      return { key: 'rpe.diag.negativeUnsigned', params };
    case 'tripleZeroDen':
      return { key: 'rpe.diag.tripleZeroDen', params };
    case 'bom':
      return { key: 'rpe.diag.bom', params };
    case 'leadingWhitespace':
      return { key: 'rpe.diag.leadingWhitespace', params };
    case 'invalidUtf8':
      return { key: 'rpe.diag.invalidUtf8', params };
    case 'noMetaSubstring':
      return { key: 'rpe.diag.noMetaSubstring', params };
    case 'controlMissingValueKey':
      return { key: 'rpe.diag.controlMissingValueKey', params };
  }
}

const I32_MIN = -2147483648n;
const I32_MAX = 2147483647n;
const I64_MIN = -(2n ** 63n);
const I64_MAX = 2n ** 63n - 1n;

/**
 * Phira's effective RPEVersion (F§2.2) and the raw lexeme to preserve. Integers outside i64 behave like floats in
 * serde_json (as_i64 fails -> 160); inside i64 they are cast to i32 (two's complement wrap). serde_json reads the
 * lexeme `-0` as the float -0.0: effective 160, kept as written like `170.0`. A string with an unpaired surrogate
 * escape refuses the chart (the decoder reports it); nothing to preserve, its effective value is 160.
 */
export function rpeVersionOf(
  node: JsonNode | undefined,
  text: string,
): { readonly effective: number; readonly raw: RawNumberLexeme | null } {
  if (node === undefined) return { effective: 160, raw: null };
  if (node.k === 'num') {
    // Beyond the f64 range (`1e400`): Phira refuses the chart (decoder diagnostic); nothing to preserve.
    if (!Number.isFinite(node.v)) return { effective: 160, raw: null };
    if (!isSerdeIntegerLexeme(node)) return { effective: 160, raw: { kind: 'float', lexeme: node.lex } };
    const big = BigInt(node.lex);
    if (big < I64_MIN || big > I64_MAX) return { effective: 160, raw: null };
    return { effective: Number(BigInt.asIntN(32, big)), raw: null };
  }
  if (node.k === 'str') {
    if (hasLoneSurrogate(node.v)) return { effective: 160, raw: null };
    const lexeme = stringTokenAt(text, node.at) ?? JSON.stringify(node.v);
    const m = /^[+-]?\d+$/.exec(node.v);
    let effective = 160;
    if (m !== null) {
      const big = BigInt(node.v);
      if (big >= I32_MIN && big <= I32_MAX) effective = Number(big);
    }
    return { effective, raw: { kind: 'string', lexeme } };
  }
  return { effective: 160, raw: null };
}

function findMember(node: JsonNode, key: string): JsonNode | undefined {
  if (node.k !== 'obj') return undefined;
  let found: JsonNode | undefined;
  for (const [k, v] of node.entries) if (k === key) found = v;
  return found;
}

function decodeText(bytes: Uint8Array, diags: DecodeDiagnostic[]): string {
  try {
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    diags.push({ severity: 'phiraRejects', path: '', code: 'invalidUtf8' });
    return new TextDecoder('utf-8', { fatal: false, ignoreBOM: true }).decode(bytes);
  }
}

export const decodeRpe: DecodeRpeFn = (bytes) => {
  const pre: DecodeDiagnostic[] = [];
  const text = decodeText(bytes, pre);
  const parsed = parseJsonLossless(text);
  if (!parsed.ok) throw new RpeJsonSyntaxError(parsed.error);
  if (parsed.leading.bom) pre.push({ severity: 'phiraRejects', path: '', code: 'bom' });
  if (parsed.leading.whitespace) pre.push({ severity: 'phiraRejects', path: '', code: 'leadingWhitespace' });
  if (!text.includes('"META"')) pre.push({ severity: 'phiraRejects', path: '', code: 'noMetaSubstring' });
  const r = new Reader();
  const chart = readChart(r, parsed.node);
  const metaNode = findMember(parsed.node, 'META');
  const ver = rpeVersionOf(metaNode === undefined ? undefined : findMember(metaNode, 'RPEVersion'), text);
  return {
    chart,
    diagnostics: [...pre, ...r.diagnostics],
    rpeVersionEffective: ver.effective,
    rpeVersionRaw: ver.raw,
  };
};

// ---------------------------------------------------------------------------------------------------------------
// Fragments
// ---------------------------------------------------------------------------------------------------------------

/** Value key of a control curve fragment ('skew' for skewControl, which Phira ignores). */
export type FragmentControlKey = ControlValueKey | 'skew';

/** RpeFragmentIr variants (CR-W1-07a-3); the decoder's `list` is always an RPE event list key. */
export type RpeEventsFragment = Extract<RpeFragmentIr, { kind: 'events' }> & { readonly list?: EventListKey };
export type RpeControlsFragment = Extract<RpeFragmentIr, { kind: 'controls' }>;

export interface RpeFragmentOptions {
  /** The list the events belong to (value type, speed rules); inferred from the values when absent. */
  readonly list?: EventListKey;
  /** The value key of a control curve; inferred from the points when absent. */
  readonly controlKey?: FragmentControlKey;
  /**
   * Tempo for seconds-based slicing (default: 60 BPM, seconds = beats). Points with an invalid beat or tempo are
   * left out and the rest sorted by beat.
   */
  readonly bpmList?: ReadonlyArray<{ readonly bpm: number; readonly startTime: Triple }>;
  /** Speed integrator for speed lists (default 'legacy'). */
  readonly speedMode?: RpeSpeedMode;
  /** Texture classification for the 'line' fragment (default: by file extension). */
  readonly textureKind?: (path: string) => 'gif' | 'image' | null;
}

export interface RpeFragmentDecodeResult {
  readonly value: Json;
  /** The fragment's IR when it is represented exactly; null when it is empty or normalisation would change it. */
  readonly ir: RpeFragmentIr | null;
  readonly diagnostics: readonly DecodeDiagnostic[];
  /** What normalisation changes ('normalized' / 'changed' entries; empty when `ir` is not null). */
  readonly report: readonly ImportReportEntry[];
  /** The normalised IR (also when normalisation changed something); null when nothing can be represented. */
  readonly normalized: RpeFragmentIr | null;
}

/**
 * The fragment's tempo: the given BPM points with a valid beat and a positive tempo, sorted by beat (a document's
 * tempo map always is; anything else would make seconds run backwards), else 60 BPM from beat 0.
 */
function fragmentTempo(
  list: RpeFragmentOptions['bpmList'],
): Array<{ readonly bpm: number; readonly startTime: Triple }> {
  const valid = (list ?? [])
    .map((p, i) => ({ p, i, b: Beats.tryFromTriple(p.startTime) }))
    .filter((x) => x.b !== null && Number.isFinite(x.p.bpm) && x.p.bpm > 0)
    .sort((x, y) => (x.b !== null && y.b !== null ? Beats.cmp(x.b.beat, y.b.beat) : 0) || x.i - y.i)
    .map((x) => x.p);
  return valid.length > 0 ? valid : [{ bpm: 60, startTime: [0, 0, 1] }];
}

function fragmentEnv(opts: RpeFragmentOptions, report: NormReport): NormEnv {
  const bpm = fragmentTempo(opts.bpmList);
  const map = createTempoMap(bpm);
  const first = Beats.tryFromTriple(bpm[0]?.startTime ?? [0, 0, 1])?.beat ?? Beats.ZERO;
  return {
    report,
    time: listTime({ sec: (t) => map.secAtTriple(t), out: (t) => t, zeroBeat: first, shift: null }),
    speedMode: opts.speedMode ?? 'legacy',
    textureKind: opts.textureKind ?? defaultTextureKind,
    sidecar: false,
  };
}

function inferValueKind(node: JsonNode): ValueKind {
  if (node.k !== 'arr') return 'f32';
  for (const item of node.items) {
    if (item.k !== 'obj') continue;
    const start = item.entries.find(([k]) => k === 'start')?.[1];
    if (start?.k === 'str') return 'string';
    if (start?.k === 'arr') return 'rgb';
    if (start?.k === 'num') return 'f32';
  }
  return 'f32';
}

function inferControlKey(node: JsonNode): FragmentControlKey {
  const counts = new Map<FragmentControlKey, number>();
  const candidates: readonly FragmentControlKey[] = [...Object.values(CONTROL_CURVES), 'skew'];
  if (node.k === 'arr') {
    for (const item of node.items) {
      if (item.k !== 'obj') continue;
      for (const [k] of item.entries) {
        const c = candidates.find((x) => x === k);
        if (c !== undefined) counts.set(c, (counts.get(c) ?? 0) + 1);
      }
    }
  }
  let best: FragmentControlKey = 'pos';
  let bestN = 0;
  for (const c of candidates) {
    const n = counts.get(c) ?? 0;
    if (n > bestN) {
      best = c;
      bestN = n;
    }
  }
  return best;
}

function modeOf(list: EventListKey | undefined, speedMode: RpeSpeedMode): ListMode {
  if (list === 'speedEvents') return speedMode === 'rpe170' ? 'speed170' : 'speedLegacy';
  if (list === 'gifEvents') return 'gif';
  if (list === 'inclineEvents') return 'incline';
  return 'standard';
}

function defaultOf(list: EventListKey | undefined): ListValue | undefined {
  switch (list) {
    case 'colorEvents':
      return [255, 255, 255];
    case 'textEvents':
      return '';
    case 'inclineEvents':
      return 0;
    case 'paintEvents':
      return -1;
    default:
      return undefined;
  }
}

const NOT_IN_LINE_FRAGMENT = ['eventLayers', 'extended', 'notes'] as const;

/** Fragment decoder with the normalisation report and the repaired IR (see the module header). */
export function decodeRpeFragmentDetailed(
  kind: RpeFragmentKind,
  text: string,
  opts: RpeFragmentOptions = {},
): RpeFragmentDecodeResult {
  const parsed = parseJsonLossless(text);
  if (!parsed.ok) throw new RpeJsonSyntaxError(parsed.error);
  const node = parsed.node;
  // Plain JSON of the text, always writable: a number beyond the f64 range holds the largest finite double of its
  // sign and an unpaired surrogate U+FFFD, as in data a whole chart keeps (`passthroughJson`; reported below).
  const value = passthroughJson(null, node, '', 'note');
  const r = new Reader();
  const report = new NormReport();
  const env = fragmentEnv(opts, report);
  let normalized: RpeFragmentIr | null = null;
  switch (kind) {
    case 'events': {
      if (node.k !== 'arr') {
        r.wrongType('phiraRejects', '', 'eventList', node);
        break;
      }
      const valueKind = opts.list === undefined ? inferValueKind(node) : eventValueKind(opts.list);
      const events = readEventList(r, node, '', opts.list ?? null, valueKind);
      const def = defaultOf(opts.list);
      const base = {
        key: opts.list ?? null,
        kind: valueKind,
        mode: modeOf(opts.list, env.speedMode),
        path: '',
        line: undefined,
        time: env.time,
        report,
        sidecarKey: null,
      };
      const ir = normalizeEventList(events, def === undefined ? base : { ...base, def });
      const first = ir[0];
      if (first !== undefined) {
        const frag: RpeEventsFragment = {
          kind: 'events',
          ir: [first, ...ir.slice(1)] as unknown as Extract<RpeFragmentIr, { kind: 'events' }>['ir'],
        };
        normalized = opts.list === undefined ? frag : { ...frag, list: opts.list };
      }
      break;
    }
    case 'notes': {
      if (node.k !== 'arr') {
        r.wrongType('phiraRejects', '', 'noteList', node);
        break;
      }
      const notes: RpeNote[] = [];
      node.items.forEach((item, i) => {
        const n = readNote(r, item, `[${String(i)}]`);
        if (n !== null) notes.push(n);
      });
      normalized = { kind: 'notes', ir: normalizeNotes(notes, '', undefined, env) };
      break;
    }
    case 'line': {
      if (node.k !== 'obj') {
        r.wrongType('phiraRejects', '', 'line', node);
        break;
      }
      const v = new ObjView(r, node, '', 'line');
      for (const key of NOT_IN_LINE_FRAGMENT) {
        if (v.has(key)) r.diag('note', key, 'wrongType', undefined, { reason: 'otherFragment' });
      }
      const fields = readLineFields(r, v);
      const line = lineFieldsToIr(fields, env);
      // Without the chart's line count only fathers no chart can have are refused (below -1, beyond i32): the
      // parent is cleared, as for whole charts, so the repaired line always encodes.
      if (fatherNeverValid(line.father)) {
        report.add('changed', { key: 'rpe.report.fatherInvalid', params: { father: line.father } }, 'father');
        normalized = { kind: 'line', ir: { ...line, father: -1 } };
      } else {
        normalized = { kind: 'line', ir: line };
      }
      break;
    }
    case 'controls': {
      if (node.k !== 'arr') {
        r.wrongType('phiraRejects', '', 'controlList', node);
        break;
      }
      const controlKey = opts.controlKey ?? inferControlKey(node);
      // skewControl is RPE-only (Phira skips it): read like Phira's curves with 'skew' as the value key, every
      // finding a note, and 0 (RPE's default) for a point without its value.
      const skew = controlKey === 'skew';
      const points = readControls(r, node, '', controlKey, skew ? 'note' : 'phiraRejects');
      const frag: RpeControlsFragment = {
        kind: 'controls',
        ir: controlsToIr(points, { valueKey: controlKey, def: skew ? 0 : 1, report, path: '' }),
        controlKey,
      };
      normalized = frag;
      break;
    }
  }
  // Whole-chart report rules (normalizeRpe): what the decoder had to replace, round or drop is reported ('changed'
  // where Phira rejects or panics, 'normalized' for data Phira skips), so `ir` is null whenever a value changed.
  // A fragment that cannot be represented at all has no report: `normalized` is null and the diagnostics say why.
  const found = new NormReport();
  if (normalized !== null) reportDiagnostics(r.diagnostics, found);
  const entries = [...found.entries(), ...report.entries()];
  return {
    value,
    ir: entries.length === 0 ? normalized : null,
    diagnostics: r.diagnostics,
    report: entries,
    normalized,
  };
}

export const decodeRpeFragment: DecodeRpeFragmentFn = (kind, text) => decodeRpeFragmentDetailed(kind, text);
